import React, { useEffect, useState } from "react";
import CloseButton from "@/components/Buttons/CloseButton/CloseButton";

interface NavFlyoutPanelProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const TRANSITION_MS = 250;

/**
 * Desktop/tablet nav-rail flyout: docks immediately to the right of the
 * 72px icon rail (see .nav-rail in Navigation.css) instead of centering
 * as a modal. Fades in on open and fades out on close.
 */
const NavFlyoutPanel: React.FC<NavFlyoutPanelProps> = ({ title, isOpen, onClose, children }) => {
  // Stay mounted through both the open and close transitions instead of
  // popping in/out instantly. Defer the "open" class a frame so the opening
  // transition actually runs instead of snapping straight to its end state.
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      const raf = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setIsVisible(false);
    const timeout = setTimeout(() => setIsMounted(false), TRANSITION_MS);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Desktop/tablet only (see .nav-rail-active media query): push page content over
  // instead of floating the flyout on top of it.
  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("nav-flyout-open");
    return () => document.body.classList.remove("nav-flyout-open");
  }, [isOpen]);

  if (!isMounted) return null;

  return (
    <>
      <div
        className="fixed inset-y-0 right-0 z-[1000] md:hidden"
        style={{ left: 72 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`fixed inset-y-0 left-[72px] z-[1001] w-[380px] max-w-[90vw] overflow-y-auto border-r border-[#e5e7eb] bg-white md:shadow-none shadow-xl transition-opacity duration-[250ms] ease-out ${isVisible ? "opacity-100" : "opacity-0"}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between px-6 pb-4 pt-6">
          <h2 className="font-lora text-[26px] font-semibold text-gray-900">{title}</h2>
          <CloseButton onClick={onClose} ariaLabel={`Close ${title}`} size={20} />
        </div>
        {children}
      </div>
    </>
  );
};

export default NavFlyoutPanel;
